import { useState } from "react";
import { useAtomValue } from "jotai";
import { AnimatePresence, motion } from "motion/react";
import { Loader } from "lucide-react";

import { AccountType, MessageType } from "shared/types";
import { cn } from "shared/lib";
import { noticeboardDataVar } from "@/variables/globalVar";

import { useMediaQuery } from "shared/hooks";
import {
  useAccount,
  useFindMessageOfType,
  useLatestMessage,
  useMessages,
  useShouldRenderSideBySide,
} from "@/hooks";
import { useRenderType, RenderType } from "./hooks";

import { StepLayout } from "@/components/wizard/StepLayout";
import { RenderWizardStep } from "./RenderWizardStep";
import { RoutingStep } from "./widgets";
import { SystemErrorDialog } from "@/components/wizard/systemErrorDialog";
import { AnalysisLayoutContainer } from "@/components/analysisLayout";

export function Wizard() {
  const [routerAnimating, setRouterAnimating] = useState(false);

  const { conversation } = useAccount();
  const { messages } = useMessages();

  const latestAssistantMessage = useLatestMessage({
    senderAccountType: AccountType.ASSISTANT,
  });

  const routingQuestionMessage = useFindMessageOfType({
    messageType: MessageType.ROUTING_QUESTION,
  });

  const noticeboardData = useAtomValue(noticeboardDataVar);

  const md = useMediaQuery({
    query: "(min-width: 768px)",
  });

  const shouldRenderSideBySide = useShouldRenderSideBySide();

  const showLoadingIndicator = !conversation || !latestAssistantMessage;

  const renderType = useRenderType(conversation);

  const render = () => {
    if (showLoadingIndicator) {
      return (
        <div className="flex h-full w-full items-center justify-center">
          <Loader className="text-textSubdued h-[48px] w-[48px] animate-spin" />
        </div>
      );
    }

    switch (renderType) {
      case RenderType.DEBUG_MESSAGE:
      case RenderType.QUESTIONS:
        const disableSideBySide = [
          MessageType.REVIEW_PROPOSAL,
          MessageType.FINALISE_DETAILS,
        ].includes(latestAssistantMessage?.message_type as any);

        return (
          <StepLayout disableSideBySide={disableSideBySide}>
            <RenderWizardStep
              messages={messages}
              message={latestAssistantMessage}
            />
          </StepLayout>
        );
      case RenderType.SYSTEM_ERROR:
        return (
          <StepLayout>
            <SystemErrorDialog />
          </StepLayout>
        );
      case RenderType.ROUTER:
        return (
          <AnimatePresence>
            <motion.div
              onAnimationStart={() => {
                setRouterAnimating(true);
              }}
              onAnimationComplete={() => {
                setRouterAnimating(false);
              }}
              animate={{ transition: { duration: 0.5 } }}
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <StepLayout className="relative" disableSideBySide>
                <RoutingStep message={routingQuestionMessage} />
              </StepLayout>
            </motion.div>
          </AnimatePresence>
        );
      case RenderType.REPORT:
        if (!routerAnimating) {
          return (
            <AnalysisLayoutContainer
              conversation={conversation}
              messages={messages}
              noticeboardData={noticeboardData}
            />
          );
        }
    }
  };

  return (
    <section
      aria-label="chat"
      className={cn(
        "flex grow flex-col px-5 py-5 md:pt-2",
        !md ? "bg-interfaceBgLightPaper" : "bg-interfaceBgDarkPaper",
      )}
      role="region"
    >
      <div
        className={cn(
          "flex grow flex-col rounded-xl",
          md || shouldRenderSideBySide ? "bg-interfaceBgLightPaper" : "",
        )}
      >
        {render()}
      </div>
    </section>
  );
}
